// App
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';

// Components
import Checkbox from 'app/shared/modules/MobileSRP/__BaseComponents__/Checkbox/index';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Misc / utils
import {
  GeneralFilterHeader,
  GeneralFilterLabel,
  GeneralFilterResetButton,
  GeneralFilterSection,
} from 'app/shared/modules/filter/FilterBar/styles';
import { DEFAULT } from 'app/shared/models/Filter';
import * as S from './styles';
import { emitTrackPetsFilterEvent } from 'app/shared/models/Clickstream/FilterClickstreamEvents';

type PetState = {
  cats: boolean;
  dogs: boolean;
};

type RootState = {
  filter: {
    pets: PetState;
  };
};

type PetFilterProps = {
  /** Whether to show the Reset button (typically shown in desktop filter panel) */
  showResetButton?: boolean;
};

/**
 * Pet-friendly filter component for rental listings
 *
 * ## Purpose
 * Allows users to filter listings based on pet policies - whether the property
 * allows dogs, cats, or both. Critical filter for pet owners searching for rentals.
 *
 * ## Business Context
 * Pet-friendly housing is a major pain point for renters with pets. Many users have
 * this as a hard requirement and will immediately filter to pet-friendly listings.
 * We track usage of this filter separately for analytics to understand demand for
 * pet-friendly housing in different markets.
 *
 * ## Filter Behavior
 * - **Both unchecked**: No pet filter applied (shows all listings)
 * - **Dogs only**: Shows listings that allow dogs (may also allow cats)
 * - **Cats only**: Shows listings that allow cats (may also allow dogs)
 * - **Both checked**: Shows listings that allow EITHER dogs OR cats (union, not intersection)
 *
 * ## Analytics Integration
 * Emits tracking events when pet filters are enabled to help product team understand:
 * - Pet owner search volume by market
 * - Conversion rates for pet owners
 * - Supply/demand gaps for pet-friendly rentals
 *
 * ## Implementation Notes
 * - Uses `isLoaded` pattern to prevent Redux update on initial mount
 * - Auto-syncs to Redux on every change (no Apply button needed)
 * - Optional Reset button for clearing all pet filters
 *
 * @param props - Component props
 * @param props.showResetButton - Whether to display the Reset button (desktop variant)
 */
const PetFilter = ({ showResetButton = false }: PetFilterProps) => {
  // Redux
  const petState = useSelector((state: RootState) => state.filter.pets);
  const [filterState, setFilterState] = useState<PetState>(petState);

  // Hooks
  const { reduxOnlyChange, resetFilter } = useFilterChange();

  /**
   * Resets both cat and dog filters to default (unchecked) state
   * Dispatches Redux action and updates local state
   */
  const handleResetClick = useYieldCallback(() => {
    resetFilter({ pets: DEFAULT.pets });
    setFilterState({ cats: false, dogs: false });
  });

  /**
   * Emits analytics tracking event when pet filter is enabled
   *
   * Only tracks when filter is turned ON, not when turned off.
   * This helps analytics understand when users are actively seeking pet-friendly housing.
   *
   * @param state - The new state of the pet filter (true = enabled, false = disabled)
   */
  const handleTracking = (state: boolean) => {
    const isTrue = state === true;

    if (isTrue) {
      emitTrackPetsFilterEvent({ triggerLocation: 'search_results' });
    }
  };

  /**
   * Handles clicking a pet checkbox (dogs or cats)
   *
   * Toggles the selected pet option and emits tracking event if enabling the filter.
   * Both checkboxes can be selected simultaneously (OR logic).
   */
  const yieldHandleOptionClick = useYieldCallback((option: keyof PetState) => {
    const newFilterState = { ...filterState, [option]: !filterState[option] };
    setFilterState(newFilterState);
    reduxOnlyChange({ pets: newFilterState });
    handleTracking(!filterState[option]);
  });

  /**
   * Event handler wrapper that extracts pet type from checkbox data attribute
   * Guards against missing data attributes before delegating to yieldHandleOptionClick
   */
  const handleOptionClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const option = e.currentTarget.getAttribute('data-option-value');

    // Guard against null/undefined option
    if (!option) return;

    yieldHandleOptionClick(option as keyof PetState);
  };

  return (
    <GeneralFilterSection>
      <GeneralFilterHeader>
        <GeneralFilterLabel>Pets</GeneralFilterLabel>
        {showResetButton && (
          <GeneralFilterResetButton onClick={handleResetClick} aria-label={'Reset all active pet filters'}>
            Reset
          </GeneralFilterResetButton>
        )}
      </GeneralFilterHeader>
      <S.FlexSection>
        <Checkbox
          data-option-value={'dogs'}
          onChange={handleOptionClick}
          isChecked={filterState.dogs}
          ariaLabel={'Allows dogs'}
          flex
        >
          Allows dogs
        </Checkbox>
        <Checkbox
          data-option-value={'cats'}
          onChange={handleOptionClick}
          isChecked={filterState.cats}
          ariaLabel={'Allows cats'}
          flex
        >
          Allows cats
        </Checkbox>
      </S.FlexSection>
    </GeneralFilterSection>
  );
};

export default PetFilter;
